import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { Icon as KDSIcon } from '@kizen/kds/Icon';
import { Spacer } from '@kizen/kds/Spacer';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Button } from '@kizen/kds/Button';
import {
  AddPaymentContactModal,
  communicationTypeToLabel,
} from '../components/AddPaymentContactModal';
import useModal from '__components/Modals/useModal';
import { toastVariant, useToast } from '__components/ToastProvider';
import BillingService from 'services/BillingService';
import Icon from '__components/Kizen/Icon';
import IconButtonMenu from '__components/Kizen/IconButtonMenu';
import { colorsButton } from 'app/colors';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import { useBillingPermissions } from 'ts-components/hooks/permissions/billing';
import { Billing } from 'queries/query-keys';

type CommunicationTypeKey = 'receipts' | 'overage_warnings' | 'overage_alerts';

export const PaymentContact = () => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [editPaymentContact, setEditPaymentContact] = useState(null as any);
  const [deletePaymentContactId, setDeletePaymentContactId] = useState('');

  const { data: paymentContactData, refetch: refetchPaymentContact } = useQuery(
    {
      queryKey: Billing.PAYMENT_CONTACT,
      queryFn: () => BillingService.getPaymentContact(),
    }
  );

  const [addContactModalProps, , addContactModal] = useModal({});

  const addPaymentContactMutation = useMutation({
    mutationFn: (paymentContact: any) =>
      BillingService.createPaymentContact(paymentContact),
    onSuccess: () => {
      refetchPaymentContact();
      addContactModal.hide();
    },
    onError: (error: any) => {
      if (error?.response?.data?.email.length > 0) {
        showToast({
          message: error?.response?.data?.email[0],
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: t('Failed to add payment contact'),
          variant: toastVariant.FAILURE,
        });
      }
    },
  });

  const deletePaymentContactMutation = useMutation({
    mutationFn: (contactId: string) =>
      BillingService.deletePaymentContact(contactId),
    onSuccess: () => {
      refetchPaymentContact();
      showToast({
        message: t('Payment contact deleted successfully'),
        variant: toastVariant.SUCCESS,
      });
    },
    onError: (error: any) => {
      if (typeof error?.response?.data === 'string') {
        showToast({
          message: error?.response?.data,
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: t('Failed to delete payment contact'),
          variant: toastVariant.FAILURE,
        });
      }
    },
  });

  const handleDeleteConfirm = useCallback(async () => {
    await deletePaymentContactMutation.mutate(deletePaymentContactId);
  }, [deletePaymentContactMutation, deletePaymentContactId]);

  const [confirmDeletionModalProps, , confirmDeletionModal] = useModal({
    handleSubmit: handleDeleteConfirm,
  });

  const editPaymentContactMutation = useMutation({
    mutationFn: ({
      contactId,
      paymentContact,
    }: {
      contactId: string;
      paymentContact: any;
    }) => BillingService.updatePaymentContact(contactId, paymentContact),

    onSuccess: () => {
      refetchPaymentContact();
      addContactModal.hide();
      setEditPaymentContact(false);
      showToast({
        message: t('Payment contact updated successfully'),
        variant: toastVariant.SUCCESS,
      });
    },
    onError: (error: any) => {
      if (typeof error?.response?.data === 'string') {
        showToast({
          message: error?.response?.data,
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: t('Failed to update payment contact'),
          variant: toastVariant.FAILURE,
        });
      }
    },
  });

  const handlePaymentContactConfirm = useCallback(
    (contact: any) => {
      editPaymentContact
        ? editPaymentContactMutation.mutate({
            contactId: editPaymentContact.id,
            paymentContact: contact,
          })
        : addPaymentContactMutation.mutate(contact);
    },
    [editPaymentContact, editPaymentContactMutation, addPaymentContactMutation]
  );

  const billingPermissions = useBillingPermissions();

  const isReadOnly =
    billingPermissions.canViewPaymentMethods &&
    !billingPermissions.canEditPaymentMethods;

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-end gap-[10px]">
          <Typography variant="header" size="xl" weight="semibold">
            {t('Payment Contacts')}
          </Typography>
          <KDSIcon
            size="sm"
            icon="message-info-circle"
            tooltipEnabled
            tooltipTitleOverride=""
            tooltipDescriptionOverride={t(
              'Set additional email addresses to receive specific billing emails like overages and payment confirmations. Team Members with the Billing Settings permission will always get these emails.'
            )}
          />
        </div>
        {isReadOnly ? null : (
          <div className="text-right">
            <Button
              onClick={addContactModal.show}
              maxWidth={false}
              variant="text"
              leftIcon="action-add"
              size="xs"
            >
              {t('Add Payment Contact')}
            </Button>
          </div>
        )}
      </div>
      <Spacer size={20} />
      <div className="grid grid-cols-[2fr_3fr_1fr] gap-[10px] pb-3 border-b border-border-medium-light">
        <div className="flex justify-start min-w-0">
          <Typography weight="semibold" size="md">
            {t('Email Address')}
          </Typography>
        </div>
        <div className="flex justify-start min-w-0">
          <Typography weight="semibold" size="md">
            {t('Communication Received')}
          </Typography>
        </div>
        <div className="flex justify-end min-w-0">
          <Typography weight="semibold" size="md">
            {t('Actions')}
          </Typography>
        </div>
      </div>
      <Spacer size={10} />
      <div
        className="mb-0 w-[calc(100%+40px)] ml-[-20px]"
        style={{
          borderBottom: '1px solid var(--color-border-medium-light)',
        }}
      ></div>
      {paymentContactData?.map((contact: any) => (
        <div
          key={contact.id}
          className="grid grid-cols-[2fr_3fr_1fr] gap-[10px] py-3"
        >
          <div className="flex justify-start items-center min-w-0">
            <Typography truncate size="md" className="w-full">
              {contact.email}
            </Typography>
          </div>
          <div className="flex justify-start items-center min-w-0">
            <Typography size="md" truncate className="w-full">
              {contact.communication_types
                .map((communicationType: string) => {
                  const labels = communicationTypeToLabel(t);
                  return (
                    labels[communicationType as CommunicationTypeKey] ||
                    communicationType
                  );
                })
                .join(', ')}
            </Typography>
          </div>
          <div className="flex justify-end items-center min-w-0">
            <IconButtonMenu
              title={t('Edit Options')}
              position="right"
              sizing="dense"
              options={[
                {
                  value: 'edit',
                  label: t('Edit'),
                },
                {
                  value: 'delete',
                  label: t('Delete'),
                },
              ]}
              color={colorsButton.iconGray}
              onChange={(option: {
                value: 'edit' | 'delete';
                label: string;
              }) => {
                if (option.value === 'edit') {
                  setEditPaymentContact(contact);
                  addContactModal.show();
                } else if (option.value === 'delete') {
                  confirmDeletionModal.show();
                  setDeletePaymentContactId(contact.id);
                }
              }}
              restrictCloseHandler
            >
              <Icon icon="three-dot" />
            </IconButtonMenu>
          </div>
        </div>
      ))}
      {confirmDeletionModalProps.show ? (
        <ConfirmDeletionModal {...confirmDeletionModalProps}>
          {t('This will permanently delete this payment contact.')}
        </ConfirmDeletionModal>
      ) : null}

      {addContactModalProps.show ? (
        <AddPaymentContactModal
          {...addContactModalProps}
          existing={editPaymentContact}
          onConfirm={handlePaymentContactConfirm}
        />
      ) : null}
    </>
  );
};
